<script setup lang="ts">
import { DivisibleSection } from '@precon-tools-suite-by-shared/primevue-receivers';
import {
    type UserConfigItem,
    useAppConfigStore,
} from '../stores/app-config-store';
import { useI18n } from 'vue-i18n';
import { type FunctionalComponent, h } from 'vue';
import MenuModeSelect from '../components/MenuModeSelect.vue';
import RippleSwitch from '../components/RippleSwitch.vue';
import ScaleSelect from '../components/ScaleSelect.vue';
import InputStyleSelect from '../components/InputStyleSelect.vue';
import ColorSchemeSelect from '../components/ColorSchemeSelect.vue';
import TransparentTopbarSwitch from '../components/TransparentTopbarSwitch.vue';
import LocaleSelect from '../components/LocaleSelect.vue';
import DismissableMaskSwitch from '../components/DismissableMaskSwitch.vue';
import CloseOnEscapeSwitch from '../components/CloseOnEscapeSwitch.vue';

const configStore = useAppConfigStore();

const ConfigItem: FunctionalComponent<{ item: UserConfigItem }> = ({
    item,
}) => {
    const props = {
        class: ['app-config-item', `app-config-item-${item}`],
    };

    switch (item) {
        case 'menu-mode':
            return h(MenuModeSelect, props);
        case 'ripple':
            return h(RippleSwitch, props);
        case 'scale':
            return h(ScaleSelect, props);
        case 'input-style':
            return h(InputStyleSelect, props);
        case 'color-scheme':
            return h(ColorSchemeSelect, props);
        case 'transparent-topbar':
            return h(TransparentTopbarSwitch, props);
        case 'locale':
            return h(LocaleSelect, props);
        case 'dismissable-mask':
            return h(DismissableMaskSwitch, props);
        case 'close-on-escape':
            return h(CloseOnEscapeSwitch, props);
    }
};

const { t } = useI18n();
</script>

<template>
    <DivisibleSection :title="t('title')" v-bind="$attrs">
        <template #description>
            {{ t('description') }}
        </template>
        <template
            v-for="configItem in configStore.userConfigItems"
            :key="configItem"
        >
            <h5
                class="app-config-item-title"
                :class="`app-config-item-${configItem}-title`"
            >
                {{ t(`items.${configItem}`) }}
            </h5>
            <ConfigItem :item="configItem" />
        </template>
    </DivisibleSection>
</template>
<!-- eslint-disable @intlify/vue-i18n/no-unused-keys -->
<i18n lang="json">
{
    "en": {
        "title": "App Configuration",
        "description": "These settings only apply to your current browser.",
        "items": {
            "color-scheme": "Color Scheme",
            "ripple": "Ripple Effect",
            "menu-mode": "Menu",
            "input-style": "Input Style",
            "transparent-topbar": "Topbar",
            "scale": "Component Scale",
            "locale": "Language",
            "dismissable-mask": "Close Form on Outside Click",
            "close-on-escape": "Close Form on Escape"
        }
    },
    "nl": {
        "title": "App Configuratie",
        "description": "De onderstaande instellingen zijn alleen van toepassing in uw huidige browser.",
        "items": {
            "color-scheme": "Kleurenschema",
            "ripple": "Visuele Effecten",
            "menu-mode": "Menu",
            "input-style": "Invulvelden",
            "transparent-topbar": "Bovenbalk",
            "scale": "Schaal",
            "locale": "Taal",
            "dismissable-mask": "Formulier sluiten bij klik buiten",
            "close-on-escape": "Formulier sluiten met Escape"
        }
    },
    "fr": {
        "title": "Configuration d'application",
        "description": "Ces paramètres s'appliquent uniquement à votre navigateur.",
        "items": {
            "color-scheme": "Mode d'éclairage",
            "menu-mode": "Menu",
            "transparent-topbar": "Barre d'outils",
            "locale": "Langue",
            "input-style": "Champs de saisie",
            "scale": "Échelle",
            "ripple": "Effets visuels",
            "dismissable-mask": "Fermer le formulaire en cliquant à l'extérieur",
            "close-on-escape": "Fermer le formulaire avec Échap"
        }
    }
}
</i18n>
